import { useEffect, useMemo, useState, useCallback } from 'react'
import { Plus, Edit, List, Search } from 'lucide-react'
import { ColDef } from 'ag-grid-community'
import Button from '@/components/ui/Button'
import DataGrid from '@/components/data/DataGrid'
import Tag from '@/components/ui/Tag'
import MemberCreateModal from './components/MemberCreateModal'
import MemberEditModal from './components/MemberEditModal'
import InviteListModal from './components/InviteListModal'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { fetchMembers, fetchMoreMembers, clearError, updateMember, resetMemberData, setCurrentPage, setPageSize } from '@/store/memberSlice'
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import { useAlert } from '@/components/ui/Alert/exports'
import { AG_GRID_LOCALE_KR } from '@/locales/ag-grid-ko-KR'

export default function MemberManagementPage() {
  const dispatch = useAppDispatch()
  const { showError, showSuccess } = useAlert()
  
  // Local state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isInviteListModalOpen, setIsInviteListModalOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<OrgMemberResponse | null>(null)
  const [searchValue, setSearchValue] = useState('')
  
  // Redux state
  const {
    items: members,  // Renamed from items to members for component compatibility
    isLoading,
    isLoadingMore,
    error,
    currentPage,
    pageSize,
    totalItems,
    hasNext
  } = useAppSelector(state => state.member)
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Initial data loading
  useEffect(() => {
    if (selectedWorkspace?.org_id) {
      // Reset member data first
      dispatch(resetMemberData())
      // Load initial data
      dispatch(fetchMembers({
        orgId: selectedWorkspace.org_id,
        page: 1,
        perPage: pageSize,
        includeUnavailable: true,
        reset: true
      }))
    }
  }, [dispatch, selectedWorkspace?.org_id, pageSize])


  // Clear error when component mounts
  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  // Show error alerts when Redux error changes
  useEffect(() => {
    if (error) {
      showError(error)
      dispatch(clearError())
    }
  }, [error, showError, dispatch])

  const handleCreateMember = () => {
    setIsCreateModalOpen(true)
  }

  const handleInviteSuccess = () => {
    // Close modal on success
    setIsCreateModalOpen(false)

    // Optionally refresh member list to show any changes
    if (selectedWorkspace?.org_id) {
      dispatch(fetchMembers({
        orgId: selectedWorkspace.org_id,
        page: 1,
        perPage: 1000,
        includeUnavailable: true,
        reset: true
      }))
    }
  }

  const handleEditMember = (memberId: string) => {
    // ID 타입 정규화 (문자열 보장)
    const normalizedId = String(memberId).trim()

    // 다양한 방법으로 멤버 찾기 시도
    const member = members.find(m =>
      m.id === normalizedId ||
      m.id === memberId ||
      m.account_id === normalizedId ||
      m.account_id === memberId ||
      String(m.id) === normalizedId
    )

    if (member) {
      setEditingMember(member)
      setIsEditModalOpen(true)
    } else {
      // 사용자에게 알림
      showError('멤버를 찾을 수 없습니다. 목록을 새로고침 해주세요.')

      // 데이터 새로고침 시도
      if (selectedWorkspace?.org_id) {
        dispatch(fetchMembers({
          orgId: selectedWorkspace.org_id,
          page: 1,
          perPage: 1000,
          includeUnavailable: true,
          reset: true
        }))
      }
    }
  }

  const handleEditMemberSubmit = async (memberData: {
    role?: string
    available: boolean
  }) => {
    if (!editingMember?.id || !selectedWorkspace?.org_id) {
      return
    }

    try {
      await dispatch(updateMember({
        memberId: editingMember.id,
        data: {
          role: memberData.role,
          available: memberData.available
        }
      })).unwrap()
      
      // Close modal on success
      setIsEditModalOpen(false)
      setEditingMember(null)
      
      // Refresh member list
      dispatch(fetchMembers({
        orgId: selectedWorkspace.org_id,
        page: 1,
        perPage: 1000,
        includeUnavailable: true,
        reset: true
      }))
    } catch (error) {
      // Keep modal open on error so user can retry
    }
  }

  const handleEditModalClose = () => {
    setIsEditModalOpen(false)
    setEditingMember(null)
  }

  // Search handler
  const handleSearch = useCallback(() => {
    if (!selectedWorkspace?.org_id) return

    // Reset to first page and fetch with search query
    dispatch(resetMemberData())
    dispatch(fetchMembers({
      orgId: selectedWorkspace.org_id,
      page: 1,
      perPage: pageSize,
      includeUnavailable: true,
      q: searchValue.trim() || undefined, // Only include q if searchValue is not empty
      reset: true
    }))
  }, [dispatch, selectedWorkspace?.org_id, pageSize, searchValue])

  // Clear search handler
  const handleClearSearch = useCallback(() => {
    setSearchValue('')

    if (!selectedWorkspace?.org_id) return

    // Reset to first page and fetch without search query
    dispatch(resetMemberData())
    dispatch(fetchMembers({
      orgId: selectedWorkspace.org_id,
      page: 1,
      perPage: pageSize,
      includeUnavailable: true,
      reset: true
    }))
  }, [dispatch, selectedWorkspace?.org_id, pageSize])


  // Role helper functions
  // Custom clipboard formatter for DataGrid cell copy
  const cellCopyFormatter = useCallback((params: any) => {
    const colId = params.column.getColId()
    const value = params.value

    // Format status column (available)
    if (colId === 'available') {
      return value ? '활성' : '비활성'
    }

    // Format role column
    if (colId === 'role') {
      return getRoleDisplay(value)
    }

    // Format date column (created_at)
    if (colId === 'created_at') {
      if (!value) return '-'
      return new Date(value).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // Default: return raw value
    return value?.toString() || ''
  }, [])

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'owner':
        return '기관소유자'
      case 'admin':
        return '기관관리자'
      case 'normal':
        return '일반사용자'
      default:
        return role
    }
  }

  const getRoleTag = (role: string) => {
    const roleText = getRoleDisplay(role)
    let color: "primary" | "secondary" | "success" | "error" | "warning" | "info" | "gray" | "white" | "light" | "dark"

    switch (role) {
      case 'owner':
        color = 'error'  // 소유자는 빨간색
        break
      case 'admin':
        color = 'warning'  // 관리자는 주황색
        break
      case 'normal':
        color = 'gray'  // 일반은 회색
        break
      default:
        color = 'gray'
    }

    return (
      <Tag color={color} size="small">
        {roleText}
      </Tag>
    )
  }

  // AG-Grid column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '상태',
      field: 'available',
      width: 120,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        // Handle placeholder rows
        if (params.data?.isPlaceholder) {
          return <div className="h-full bg-gray-50 animate-pulse"></div>
        }

        if (params.value === undefined || params.value === null) {
          return null
        }
        return (
          <div className="flex items-center h-full">
            <Tag
              color={params.value ? "success" : "gray"}
              size="small"
            >
              ● {params.value ? '활성' : '비활성'}
            </Tag>
          </div>
        )
      }
    },
    {
      headerName: '이름',
      field: 'account_name',
      flex: 1,
      minWidth: 150,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      }
    },
    {
      headerName: '역할',
      field: 'role',
      width: 100,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded w-12"></div>
        }
        if (!params.value) {
          return null
        }
        return (
          <div className="flex items-center h-full">
            {getRoleTag(params.value)}
          </div>
        )
      }
    },
    {
      headerName: '이메일',
      field: 'account_email',
      flex: 2,
      minWidth: 200,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      }
    },
    {
      headerName: '전화번호',
      field: 'account_phone',
      flex: 1,
      minWidth: 150,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      }
    },
    {
      headerName: '가입일시',
      field: 'created_at',
      width: 180,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        if (!params.value) return '-'
        return new Date(params.value).toLocaleString('ko-KR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    },
    {
      headerName: '액션',
      field: 'actions',
      width: 100,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        // Handle placeholder rows
        if (params.data?.isPlaceholder) {
          return <div className="h-8 bg-gray-50 animate-pulse rounded w-16"></div>
        }

        // 데이터 검증 추가
        if (!params.data) {
          return null
        }

        // ID 존재 여부 확인
        const rowId = params.data.id || params.data.account_id
        if (!rowId) {
          return <span className="text-red-500 text-xs">No ID</span>
        }

        return (
          <div className="flex items-center h-full">
            <Button
              size="small"
              color="white"
              onClick={() => {
                handleEditMember(rowId)
              }}
              className="flex items-center gap-1"
            >
              <Edit className="w-3 h-3" />
              수정
            </Button>
          </div>
        )
      },
    }
  ], [members, selectedWorkspace, dispatch, showError])

  // Create virtual row data to match server total count for proper pagination
  const virtualRowData = useMemo(() => {
    const result = [...members]

    // Add placeholder rows to reach totalItems count
    const remainingCount = totalItems - members.length
    for (let i = 0; i < remainingCount; i++) {
      result.push({
        id: `placeholder-${i}`,
        // Mark as placeholder for rendering
        isPlaceholder: true
      } as any)
    }

    return result
  }, [members, totalItems])

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Filter & Action Bar - Single Row */}
      <div className="py-2 px-4 flex-shrink-0 flex items-center gap-2 flex-wrap justify-end rounded-lg mb-6 bg-[#F6F6F7]">
        {/* Search Input */}
        <div className="relative w-[280px] flex-shrink-0">
          <div
            className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
            style={{
              backgroundColor: '#FFFFFF',
              borderColor: '#EBEBEB'
            }}
          >
            <Search
              className="w-5 h-5"
              style={{ color: '#B8B8B8' }}
            />
            <input
              type="text"
              placeholder="이름, 이메일로 검색"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
              className="flex-1 bg-transparent outline-none min-w-0"
              style={{
                fontFamily: 'Pretendard GOV',
                fontSize: '16px',
                lineHeight: '1.193359375em',
                color: searchValue ? '#1B1B1B' : '#B8B8B8'
              }}
            />
            {searchValue && (
              <button
                onClick={handleClearSearch}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="검색어 지우기"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Search Button */}
        <Button
          color="dark"
          size="medium"
          onClick={handleSearch}
          className="h-10 px-[10px] flex-shrink-0 whitespace-nowrap"
        >
          검색
        </Button>

        {/* Invite List Button */}
        <Button
          color="white"
          size="medium"
          onClick={() => setIsInviteListModalOpen(true)}
          className="h-10 px-[10px] flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
        >
          <List className="w-4 h-4" strokeWidth={2} />
          초대목록
        </Button>

        {/* Invite Member Button */}
        <Button
          color="dark"
          size="medium"
          onClick={handleCreateMember}
          className="h-10 px-[10px] flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          멤버 초대
        </Button>
      </div>


      {/* Member List Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 min-h-0">
        <DataGrid
          columnDefs={columnDefs}
          rowData={virtualRowData}
          // Use client-side pagination with manual total count override
          rowModelType="clientSide"
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[20, 50, 100]}
          // Suppress built-in pagination panel to use custom one
          suppressPaginationPanel={false}
          // Enable cell copy with custom formatter
          enableCellCopy={true}
          cellCopyFormatter={cellCopyFormatter}
          cellCopyExcludeColumns={['actions']}
          // Enable drag range selection
          enableRangeSelection={true}
          rangeSelectionExcludeColumns={['actions']}
          rangeCopyFormatter={(colId, value) => {
            // Format status column (available)
            if (colId === 'available') {
              return value ? '활성' : '비활성'
            }
            // Format role column
            if (colId === 'role') {
              return getRoleDisplay(value)
            }
            // Format date column (created_at)
            if (colId === 'created_at') {
              if (!value) return '-'
              return new Date(value).toLocaleString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            }
            // Default: return raw value
            return value?.toString() || '-'
          }}
          // Override pagination info to show server total
          localeText={{
            ...AG_GRID_LOCALE_KR,
            // Custom pagination text showing server total
            paginationOf: `총 ${totalItems.toLocaleString()}명`,
            page: '페이지',
            to: '-',
            of: `중`
          }}
          // Pagination changed handler for incremental loading
          onPaginationChanged={async (params: any) => {
            const api = params.api
            const currentPage = api.paginationGetCurrentPage() + 1 // AG-Grid uses 0-based
            const currentPageSize = api.paginationGetPageSize()

            // Handle page size changes
            if (currentPageSize !== pageSize && selectedWorkspace?.org_id) {
              dispatch(setPageSize(currentPageSize))
              dispatch(setCurrentPage(1))
              dispatch(resetMemberData())
              dispatch(fetchMembers({
                orgId: selectedWorkspace.org_id,
                page: 1,
                perPage: currentPageSize,
                includeUnavailable: true,
                reset: true
              }))
              return
            }

            // Handle page navigation - check if we need more data
            const startIndex = (currentPage - 1) * pageSize
            const endIndex = startIndex + pageSize

            // Load data if we're viewing placeholder rows
            if (endIndex > members.length && hasNext && selectedWorkspace?.org_id) {
              await dispatch(fetchMoreMembers({
                orgId: selectedWorkspace.org_id,
                page: currentPage,
                perPage: pageSize,
                includeUnavailable: true
              }))
            }
          }}
          // Override total row count for pagination calculation
          paginationNumberFormatter={(params: any) => {
            // This ensures AG-Grid calculates pagination based on server total
            return params.value.toLocaleString()
          }}
          // Row ID and styling
          getRowId={(params: any) => {
            if (!params.data) {
              return null
            }
            const id = params.data.id || params.data.account_id
            return id
          }}
          headerHeight={40}
          rowHeight={42}
          height="calc(100vh - 200px)"
          getRowStyle={(params) => {
            // 데이터가 없으면 기본 스타일 반환
            if (!params.data) {
              return undefined
            }

            // 비활성화된 멤버의 경우 시각적으로 구분
            if (!params.data.available) {
              return {
                opacity: 0.6,
                backgroundColor: '#f9f9f9'
              }
            }
            return undefined
          }}
          // Loading state
          loading={isLoading || isLoadingMore}
        />
      </div>

      {/* Member Invite Modal */}
      <MemberCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />

      {/* Member Edit Modal */}
      <MemberEditModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSubmit={handleEditMemberSubmit}
        member={editingMember}
        isLoading={isLoading}
        error={error}
      />

      {/* Invite List Modal */}
      <InviteListModal
        isOpen={isInviteListModalOpen}
        onClose={() => setIsInviteListModalOpen(false)}
      />
    </div>
  )
}